import { adminClient } from './client';
import type { PageContext } from '../lib/pageContext';

const SUPPRESS = { suppressGlobalErrorNotification: true } as const;

export type LoanOpsChatUiActions = {
  type?: string;
  filters?: Record<string, unknown>;
  candidates?: Array<{
    applicationId: string;
    displayName?: string;
    status?: string;
    admin_url?: string;
  }>;
} | null;

export type LoanOpsToolCall = {
  tool?: string;
  args?: Record<string, unknown>;
  result_preview?: unknown;
};

export type LoanOpsChatResponse = {
  session_id: string;
  answer_text: string;
  ui_actions?: LoanOpsChatUiActions;
  tool_calls?: LoanOpsToolCall[];
  focus_application_id?: string | null;
};

export type LoanOpsChatSessionSummary = {
  sessionId: string;
  title: string | null;
  focusApplicationId: string | null;
  updatedAt: string;
  createdAt: string;
};

export const loanOpsChatApi = {
  async chat(input: {
    message: string;
    sessionId?: string;
    /** The open page, so "why is this one stuck?" resolves without a pasted uuid. */
    pageContext?: PageContext | null;
  }): Promise<LoanOpsChatResponse> {
    const r = await adminClient.post(
      '/admin/loan-ops/chat',
      {
        message: input.message,
        session_id: input.sessionId,
        page_context: input.pageContext ?? null,
      },
      SUPPRESS,
    );
    return (r.data?.data ?? r.data) as LoanOpsChatResponse;
  },

  async listSessions(limit = 30): Promise<{ sessions: LoanOpsChatSessionSummary[] }> {
    const r = await adminClient.get('/admin/loan-ops/chat/sessions', {
      params: { limit },
      ...SUPPRESS,
    });
    return (r.data?.data ?? r.data) as { sessions: LoanOpsChatSessionSummary[] };
  },

  async getSession(sessionId: string): Promise<{
    sessionId: string;
    messages: Array<{
      id: string;
      role: string;
      messageText: string;
      uiActions?: LoanOpsChatUiActions;
      toolCalls?: LoanOpsToolCall[] | null;
      createdAt: string;
    }>;
    focusApplicationId?: string | null;
  }> {
    const r = await adminClient.get(`/admin/loan-ops/chat/sessions/${encodeURIComponent(sessionId)}`, SUPPRESS);
    return (r.data?.data ?? r.data) as any;
  },
};

/** Custom event so ApplicationsPage can apply chat filters without tight coupling. */
export const LOAN_OPS_APPLY_FILTERS_EVENT = 'loan-ops:apply-filters';

export function dispatchLoanOpsApplyFilters(filters: Record<string, unknown>) {
  window.dispatchEvent(new CustomEvent(LOAN_OPS_APPLY_FILTERS_EVENT, { detail: filters }));
}

/** Build `/applications` query string from loan-ops `apply_filters` payload. */
export function applicationsSearchFromLoanOpsFilters(
  filters: Record<string, unknown>,
): URLSearchParams {
  const sp = new URLSearchParams();
  const str = (key: string) => {
    const v = filters[key];
    return typeof v === 'string' && v.trim() ? v.trim() : '';
  };
  const status = str('status');
  const stuckStep = str('stuckStep') || str('stuck_step');
  const createdFrom = str('createdFrom') || str('created_from') || str('dateFrom') || str('date_from');
  const createdTo = str('createdTo') || str('created_to') || str('dateTo') || str('date_to');
  const updatedFrom = str('updatedFrom') || str('updated_from');
  const updatedTo = str('updatedTo') || str('updated_to');

  // Match Applications list URL keys (`f`, `openedFrom`, always-present `status`).
  sp.set('f', '1');
  sp.set('status', status ? status.toUpperCase() : '');
  if (stuckStep) sp.set('stuckStep', stuckStep);
  if (createdFrom) sp.set('openedFrom', createdFrom.slice(0, 10));
  if (createdTo) sp.set('openedTo', createdTo.slice(0, 10));
  if (updatedFrom) sp.set('updatedFrom', updatedFrom.slice(0, 10));
  if (updatedTo) sp.set('updatedTo', updatedTo.slice(0, 10));
  return sp;
}
